<template>
  <div :class="`car-detail-key-stats ${statsType}`">
    <div v-if="statsType === 'expert-reviews'" class="car-detail-key-stats__box white-card relative">
      <h4 class="car-detail-key-stats__title"><img :src="CarPriceIcon" alt="An icon of a dollar sign with an arrow around it." width="20" /> Price</h4>
      <p class="car-detail-key-stats__content pb-1">{{ formattedCarPrice }} Indicative average used car price</p>
      <ExplanationModal
        class="absolute top-2 right-2"
        button-text=""
        with-icon
        :title="marketValueModalContent.title.value"
        :description="marketValueModalContent.description.value"
      />
    </div>

    <div class="car-detail-key-stats__box white-card">
      <h4 class="car-detail-key-stats__title"><img :src="ConfigIcon" alt="An icon of a cog with a checkmark." width="20" /> Configuration</h4>
      <p class="car-detail-key-stats__content" v-text="bodyDoorsSeats" />
    </div>

    <div class="car-detail-key-stats__box white-card">
      <h4 class="car-detail-key-stats__title"><img :src="FuelIcon" alt="An icon of a fuel bowser." width="20" /> Fuel</h4>
      <p class="car-detail-key-stats__content" v-text="fuelTypeAndRange" />
    </div>

    <div v-if="statsType === 'car-details'" class="car-detail-key-stats__box white-card d-flex flex-col space-between">
      <div>
        <h4 class="car-detail-key-stats__title"><img :src="EmissionsIcon" alt="An icon of two leaves." width="20" /> Emissions rating</h4>
        <p class="car-detail-key-stats__content" v-text="emissionsRating" />
      </div>
      <ExplanationModal
        :with-icon="true"
        button-text="Explanation"
        :title="emissionsModalContent.title.value"
        :description="emissionsModalContent.description.value"
      />
    </div>

    <div v-if="statsType === 'expert-reviews'" class="car-detail-key-stats__box white-card d-flex flex-col space-between">
      <div>
        <h4 class="car-detail-key-stats__title mb-1">
          <img :src="SafetyShieldIcon" width="20" alt="An icon of a shield with a star inside." />ANCAP Safety rating
        </h4>
        <AncapTestScore :date-tested="carData.ANCAP_Date_tested" :ancap-score="carData.ANCAP_Overall_score" class="compact" />
      </div>
    </div>

    <div v-if="statsType === 'car-details'" class="car-detail-key-stats__box white-card d-flex flex-col space-between">
      <div>
        <h4 class="car-detail-key-stats__title">
          <img :src="CostToRunIcon" alt="An icon of a dollar sign with an arrow around it." width="20" /> Cost to fuel / recharge
        </h4>
        <p class="car-detail-key-stats__content">
          <span v-text="costToRunText" />
        </p>
      </div>
      <ExplanationModal
        :with-icon="true"
        button-text="Explanation"
        :title="costToRunModalContent.title.value"
        :description="costToRunModalContent.description.value"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import ConfigIcon from '@/assets/img/configuration-icon.svg';
import FuelIcon from '@/assets/img/fuel-icon.svg';
import EmissionsIcon from '@/assets/img/emissions-rating-icon.svg';
import CostToRunIcon from '@/assets/img/cost-to-run-icon.svg';
import CarPriceIcon from '@/assets/img/car-price-icon.svg';
import SafetyShieldIcon from '@/assets/img/safety-shield-icon.svg';
import ExplanationModal from '../modals/ExplanationModal.vue';
import { useCarData } from '../../composables/useCarData';
import useRunningCosts from '../../composables/useRunningCosts';
import AncapTestScore from '../car-stats/AncapTestScore.vue';
import { useContentStore } from '../../foundation/stores/contentStore';

const props = defineProps({
  carData: {
    type: Object,
    required: true,
  },
  statsType: {
    type: String,
    default: 'car-details',
  },
  apiStatus: {
    type: String,
    default: 'LOADING',
  },
});

const contentStore = useContentStore();
contentStore.fetchModalDatasources();
const emissionsModalContent = contentStore.modalDatasources?.modalContentItems?.emissionsInformation;
const costToRunModalContent = contentStore.modalDatasources?.modalContentItems?.fuelCostsExplanation;
const marketValueModalContent = contentStore.modalDatasources?.modalContentItems?.marketValueCalculation;

const { bodyDoorsSeats, fuelTypeAndRange, emissionsRating, formattedCarPrice } = useCarData(props.carData);
const { costToRunText } = useRunningCosts(props.carData, {});
</script>

<style lang="scss" scoped>
.car-detail-key-stats {
  background: #fff;
  border-radius: 0.25rem;
  border: 1px solid $racq-pale-blue-border;
  margin: 0;

  display: grid;
  grid-template-rows: 1fr 1fr;
  grid-template-columns: 1fr 1fr;
  gap: 0;

  &.grid-row {
    grid-template-columns: 1fr;

    @media screen and (min-width: #{$breakpoint-lg}) {
      grid-template-rows: 1fr;
      grid-template-columns: 1fr 1fr 1fr 1fr;
    }

    .car-detail-key-stats {
      border: 1px solid $racq-pale-blue-border;
      border-radius: 0.25rem;
    }

    .car-detail-key-stats__box {
      height: 5.875rem;
      border: none;

      &:nth-of-type(1),
      &:nth-of-type(2),
      &:nth-of-type(3) {
        border-bottom: 1px solid $racq-pale-blue-border;

        @media screen and (min-width: #{$breakpoint-lg}) {
          border-right: 1px solid $racq-pale-blue-border;
          border-bottom: none;
        }
      }
    }
  }

  &__box {
    flex-grow: 1;
    height: 8.5rem;
    box-sizing: border-box;
    border-radius: 0;
    border: none;

    &:nth-of-type(1),
    &:nth-of-type(2) {
      border-bottom: 1px solid $racq-pale-blue-border;
    }

    &:nth-of-type(1) {
      border-right: 1px solid $racq-pale-blue-border;
      border-top-left-radius: 0.25rem;
    }

    &:nth-of-type(2) {
      border-top-right-radius: 0.25rem;
    }

    &:nth-of-type(3) {
      border-right: 1px solid $racq-pale-blue-border;
      border-bottom-left-radius: 0.25rem;
    }

    &:nth-of-type(4) {
      border-bottom-right-radius: 0.25rem;
    }
  }

  &__title {
    margin: 0 0 0.5rem;
    font-size: 1rem;
    color: $racq-dark-blue;
    display: flex;
    align-items: center;
    gap: 0.5rem;
    line-height: 1;

    &.mb-1 {
      margin-bottom: 0.25rem;
    }
  }

  &__content {
    margin: 0;
    line-height: 1.25;
  }
}
</style>
